import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const RANGES = [{ v: 30, l: "30 days" }, { v: 90, l: "90 days" }, { v: 365, l: "12 months" }, { v: 0, l: "All time" }];
const PAY_CLS = {
  PAID: "bg-green-100 text-green-700", PARTIALLY_PAID: "bg-amber-100 text-amber-700",
  SENT: "bg-blue-100 text-blue-700", OVERDUE: "bg-red-100 text-red-700",
  DRAFT: "bg-slate-100 text-slate-500", VOID: "bg-slate-200 text-slate-500",
  CANCELLED: "bg-slate-200 text-slate-500", NOT_INVOICED: "bg-slate-100 text-slate-400",
};

function StatCard({ label, value, sub, testid, accent }) {
  return (
    <div data-testid={testid} className={`bg-white rounded-xl border p-5 ${accent || "border-slate-200"}`}>
      <div className="text-[12px] font-[Poppins] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 font-[Poppins] font-extrabold text-[24px] text-[var(--pg-navy)]">{value}</div>
      {sub && <div className="mt-0.5 text-[12px] text-slate-400">{sub}</div>}
    </div>
  );
}

function MonthlyChart({ months }) {
  const max = Math.max(1, ...months.map((m) => m.won_value));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="revenue-monthly-chart">
      <h3 className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">Won Revenue by Month</h3>
      {months.length === 0 ? <p className="mt-4 text-[13.5px] text-slate-400">No won quotes in this period yet.</p> : (
        <div className="mt-5 flex items-end gap-3 h-[180px]">
          {months.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
              <div className="text-[11px] font-[Poppins] font-bold text-[var(--pg-navy)]">{money(m.won_value)}</div>
              <div className="w-full max-w-[46px] bg-[var(--pg-accent)] rounded-t-md mt-1 transition-[height]"
                style={{ height: `${Math.max(6, (m.won_value / max) * 120)}px` }} title={`${m.count} won · margin ${m.margin_percent}%`} />
              <div className="mt-1.5 text-[10.5px] text-slate-500">{m.month}</div>
              <div className="text-[10.5px] font-semibold text-green-700">{money(m.gross_profit)} GP</div>
              <div className="text-[10px] text-slate-400">{m.margin_percent}% mgn</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WinLoss({ wl }) {
  const rows = [
    ["accepted", "Won", "bg-green-500"],
    ["pending", "Pending (sent)", "bg-blue-400"],
    ["declined", "Declined", "bg-red-400"],
    ["expired", "Expired", "bg-slate-400"],
  ];
  const totalCount = rows.reduce((s, [k]) => s + (wl[k]?.count || 0), 0) || 1;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="revenue-winloss">
      <h3 className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">Quote Outcomes</h3>
      <div className="mt-5 space-y-3.5">
        {rows.map(([k, label, cls]) => (
          <div key={k} data-testid={`winloss-${k}`}>
            <div className="flex justify-between text-[12.5px] mb-1">
              <span className="font-medium text-slate-600">{label}</span>
              <span className="text-slate-500">{wl[k]?.count || 0} · {money(wl[k]?.value)}</span>
            </div>
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${cls}`} style={{ width: `${((wl[k]?.count || 0) / totalCount) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Revenue() {
  const [days, setDays] = useState(90);
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = days === -1 ? { date_from: custom.from, date_to: custom.to } : { days };
    if (days === -1 && !custom.from && !custom.to) return;
    api.get("/admin/revenue", { params }).then((r) => setData(r.data)).catch(() => setData({ cards: {}, win_loss: {}, monthly_won: [], recent_won: [] }));
  }, [days, custom]);

  if (!data) return <div className="text-slate-500 p-8" data-testid="revenue-loading">Loading revenue...</div>;
  const c = data.cards || {};

  return (
    <div data-testid="admin-revenue">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Revenue</h1>
        <div className="flex flex-wrap items-center gap-2">
          {days === -1 && (
            <>
              <input data-testid="revenue-date-from" type="date" className="pg-input !py-2 !w-[150px]" value={custom.from} onChange={(e) => setCustom((x) => ({ ...x, from: e.target.value }))} />
              <span className="text-slate-400 text-[13px]">to</span>
              <input data-testid="revenue-date-to" type="date" className="pg-input !py-2 !w-[150px]" value={custom.to} onChange={(e) => setCustom((x) => ({ ...x, to: e.target.value }))} />
            </>
          )}
          <select data-testid="revenue-range-select" className="pg-input !py-2.5 !w-[160px]" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {RANGES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            <option value={-1}>Custom range</option>
          </select>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="rev-card-pipeline" label="Quoted Pipeline" value={money(c.pipeline_value)} sub="Quotes sent, awaiting decision" />
        <StatCard testid="rev-card-won" label="Won Value" value={money(c.won_value)} sub={`Gross profit ${money(c.gross_profit)}`} accent="border-green-200" />
        <StatCard testid="rev-card-lost" label="Lost Value" value={money(c.lost_value)} sub="Declined + expired quotes" />
        <StatCard testid="rev-card-winrate" label="Win Rate" value={c.win_rate == null ? "—" : `${c.win_rate}%`} sub={c.win_rate == null ? "Insufficient data for this metric" : "Of decided quotes"} />
        <StatCard testid="rev-card-margin" label="Avg Margin" value={c.decided_count === 0 ? "—" : `${c.avg_margin || 0}%`} sub={c.decided_count === 0 ? "Insufficient data for this metric" : "Weighted, won quotes"} />
        <StatCard testid="rev-card-aov" label="Avg Order Value" value={c.avg_order_value == null ? "—" : money(c.avg_order_value)} sub={c.avg_order_value == null ? "Insufficient data for this metric" : "Per won quote"} />
        <StatCard testid="rev-card-collected" label="Collected" value={money(c.collected)} sub={`Invoiced ${money(c.invoiced)}`} accent="border-green-200" />
        <StatCard testid="rev-card-outstanding" label="Outstanding" value={money(c.outstanding)} sub="Awaiting payment" accent={c.outstanding ? "border-amber-200" : "border-slate-200"} />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <MonthlyChart months={data.monthly_won || []} />
        <WinLoss wl={data.win_loss || {}} />
      </div>

      <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="revenue-recent-won">
        <div className="px-6 pt-5 font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">Recent Won Orders</div>
        <table className="w-full text-left text-[13px] min-w-[1050px] mt-3">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-[Poppins]">
              <th className="px-6 py-2.5">Order</th><th className="px-4 py-2.5">Customer</th>
              <th className="px-4 py-2.5">Value</th><th className="px-4 py-2.5">Landed</th><th className="px-4 py-2.5">GP</th><th className="px-4 py-2.5">Margin</th>
              <th className="px-4 py-2.5">Payment</th><th className="px-4 py-2.5">Collected</th><th className="px-4 py-2.5">Outstanding</th><th className="px-4 py-2.5">Date</th>
            </tr>
          </thead>
          <tbody>
            {(data.recent_won || []).length === 0 && <tr><td colSpan={10} className="px-6 py-8 text-center text-slate-400">No won orders in this period.</td></tr>}
            {(data.recent_won || []).map((o) => (
              <tr key={o.order_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`rev-order-${o.order_number}`}>
                <td className="px-6 py-3">
                  <Link to={`/admin/rfqs/${o.rfq_id}`} className="font-[Poppins] font-semibold text-[var(--pg-accent)] hover:underline">{o.order_number}</Link>
                </td>
                <td className="px-4 py-3 text-[var(--pg-navy)] font-medium max-w-[160px] truncate">{o.customer_company || "—"}</td>
                <td className="px-4 py-3 font-semibold">{money(o.total)}</td>
                <td className="px-4 py-3 text-slate-500">{o.landed_cost == null ? "Not Provided" : money(o.landed_cost)}</td>
                <td className="px-4 py-3 text-green-700">{money(o.gross_profit)}</td>
                <td className="px-4 py-3 text-green-700 font-medium">{o.margin_percent}%</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-bold ${PAY_CLS[o.payment_status] || "bg-slate-100 text-slate-500"}`}>
                    {(o.payment_status || "").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3 text-green-700">{money(o.collected)}</td>
                <td className={`px-4 py-3 ${o.outstanding > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{money(o.outstanding)}</td>
                <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{(o.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
